import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Mail, Phone, Check, Archive } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pennylane/admin/enquiries`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

const fmt = (iso) =>
  iso ? new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

const STATUS_LABEL = { new: 'New', passed_on: 'Passed to agent', closed: 'Closed' };

export const PropertyEnquiriesManager = () => {
  const [data, setData] = useState({ items: [], total: 0, new_count: 0 });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(API, { headers: auth() });
      if (res.ok) setData(await res.json());
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    await fetch(`${API}/${id}`, {
      method: 'PATCH',
      headers: { ...auth(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    load();
  };

  return (
    <div className="w-full px-4 sm:px-6 pb-16" data-testid="property-enquiries-manager">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500">Buyer enquiries</p>
          <p className="text-3xl font-bold text-slate-900 tabular-nums" data-testid="enquiries-total">
            {data.total}
          </p>
          <p className="text-xs text-slate-500 mt-0.5">
            {data.new_count} waiting to be passed to an agent.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="enquiries-refresh">Refresh</Button>
      </div>

      {loading ? (
        <div className="py-10 text-center text-slate-400"><Loader2 size={18} className="animate-spin inline" /></div>
      ) : data.items.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white p-8 text-center" data-testid="enquiries-empty">
          <Mail size={20} className="text-slate-300 mx-auto mb-2" />
          <p className="text-sm text-slate-600">No buyer enquiries yet.</p>
          <p className="text-xs text-slate-500 mt-1">
            Anyone who asks about a home on the for-sale pages lands here, and you get an email straight away.
          </p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="enquiries-list">
          {data.items.map((e) => (
            <div key={e.id} className="rounded-xl border border-slate-200 bg-white p-4" data-testid={`enquiry-${e.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p className="font-semibold text-slate-900">{e.address || '(address unavailable)'}</p>
                  <p className="text-xs text-slate-500">{e.branch_name} · {fmt(e.created_at)}</p>
                </div>
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded ${
                  e.status === 'new' ? 'bg-cyan-100 text-cyan-800' : 'bg-slate-100 text-slate-600'
                }`}>
                  {STATUS_LABEL[e.status] || e.status}
                </span>
              </div>
              <p className="mt-3 text-sm text-slate-700 whitespace-pre-line">{e.message}</p>
              <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-slate-600">
                <span className="font-medium text-slate-900">{e.name}</span>
                <a href={`mailto:${e.email}`} className="inline-flex items-center gap-1 text-cyan-700 hover:underline">
                  <Mail size={12} /> {e.email}
                </a>
                {e.phone && (
                  <a href={`tel:${e.phone}`} className="inline-flex items-center gap-1 text-cyan-700 hover:underline">
                    <Phone size={12} /> {e.phone}
                  </a>
                )}
                {e.listing_url && (
                  <a href={e.listing_url} target="_blank" rel="noopener noreferrer" className="text-slate-500 hover:underline">
                    Agent listing
                  </a>
                )}
                <div className="ml-auto flex gap-2">
                  {e.status !== 'passed_on' && (
                    <Button size="sm" variant="outline" onClick={() => setStatus(e.id, 'passed_on')}
                            data-testid={`enquiry-passed-${e.id}`}>
                      <Check size={13} className="mr-1" /> Passed on
                    </Button>
                  )}
                  {e.status !== 'closed' && (
                    <Button size="sm" variant="ghost" onClick={() => setStatus(e.id, 'closed')}
                            data-testid={`enquiry-closed-${e.id}`}>
                      <Archive size={13} className="mr-1" /> Close
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PropertyEnquiriesManager;
